// Lista y reordena las actividades de una práctica de conversación.

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, ChevronUp, ChevronDown, Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useConversationActivities } from '@/hooks/useConversationData';
import { conversationBackofficeRoutes } from '@/constants/conversationBackofficeRoutes';
import type { Activity } from '@/types/story.types';
import styles from './ConversationActivityEditorPage.module.scss';

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  multiple_choice: 'Opción múltiple',
  fill_in_blank: 'Completar huecos',
  fill_in_blank_tap: 'Completar huecos (tap)',
  true_false: 'Verdadero/Falso',
  matching: 'Emparejar',
  ordering: 'Ordenar',
  open_ended: 'Respuesta abierta',
};

export function ConversationActivityEditorPage() {
  const navigate = useNavigate();
  const { resourceId } = useParams<{ resourceId: string }>();
  const { activities, isLoading, deleteActivity, reorderActivity } =
    useConversationActivities(resourceId);

  const handleBack = () => {
    if (resourceId) {
      navigate(`${conversationBackofficeRoutes.edit(resourceId)}?tab=activities`);
    } else {
      navigate(-1);
    }
  };

  const handleAddActivity = () => {
    navigate(conversationBackofficeRoutes.activityNew(resourceId ?? ''));
  };

  const handleEditActivity = (activity: Activity) => {
    navigate(conversationBackofficeRoutes.activityEdit(resourceId ?? '', activity.id));
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (window.confirm('¿Seguro que quieres eliminar esta actividad?')) {
      try {
        await deleteActivity(activityId);
      } catch (error) {
        console.error('Error deleting activity:', error);
        alert('Error al eliminar la actividad');
      }
    }
  };

  const handleMoveUp = async (activity: Activity, index: number) => {
    if (index === 0) return;
    try {
      await reorderActivity(activity.id, index - 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const handleMoveDown = async (activity: Activity, index: number) => {
    if (index === activities.length - 1) return;
    try {
      await reorderActivity(activity.id, index + 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.resourceActivityEditor}>
        <div className={styles.resourceActivityEditor__loading}>Cargando actividades...</div>
      </div>
    );
  }

  return (
    <div className={styles.resourceActivityEditor}>
      <div className={styles.resourceActivityEditor__header}>
        <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver a la conversación" />
        <Button leftIcon={<Plus />} onClick={handleAddActivity}>
          Añadir actividad
        </Button>
      </div>

      <h1 className={styles.resourceActivityEditor__title}>Editor de actividades</h1>
      <p className={styles.resourceActivityEditor__subtitle}>
        ID conversación: {resourceId} •{' '}
        {activities.length}{' '}
        {activities.length === 1 ? 'actividad' : 'actividades'}
      </p>

      <div className={styles.resourceActivityEditor__content}>
        {activities.length === 0 ? (
          <Card>
            <CardContent className={styles.resourceActivityEditor__empty}>
              <p>Sin actividades. Haz clic en "Añadir actividad" para crear una.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={styles.resourceActivityEditor__list}>
            {activities.map((activity, index) => (
              <Card key={activity.id} className={styles.resourceActivityEditor__item}>
                <CardHeader className={styles.resourceActivityEditor__itemHeader}>
                  <div className={styles.resourceActivityEditor__itemInfo}>
                    <div className={styles.resourceActivityEditor__itemBadges}>
                      <Badge variant="default">{ACTIVITY_TYPE_LABELS[activity.type] ?? activity.type}</Badge>
                      <Badge variant="info">{activity.points} pts</Badge>
                      <span className={styles.resourceActivityEditor__itemOrder}>
                        Orden: {activity.order}
                      </span>
                    </div>
                    <CardTitle>{activity.question}</CardTitle>
                    {activity.explanation && (
                      <p className={styles.resourceActivityEditor__itemExplanation}>
                        {activity.explanation}
                      </p>
                    )}
                  </div>
                  <div className={styles.resourceActivityEditor__itemActions}>
                    <div className={styles.resourceActivityEditor__itemReorder}>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<ChevronUp />}
                        onClick={() => handleMoveUp(activity, index)}
                        disabled={index === 0}
                        aria-label="Subir"
                      >
                        <span className="sr-only">Subir</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<ChevronDown />}
                        onClick={() => handleMoveDown(activity, index)}
                        disabled={index === activities.length - 1}
                        aria-label="Bajar"
                      >
                        <span className="sr-only">Bajar</span>
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Edit />}
                      onClick={() => handleEditActivity(activity)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      leftIcon={<Trash2 />}
                      onClick={() => handleDeleteActivity(activity.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationActivityEditorPage;
